/**
 * Paymint Dashboard Stats Card
 * 관리자용 Paymint 구독 및 결제 통계 카드
 */

import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../../../../config/api';

interface PaymintStats {
  subscriptions: {
    total: number;
    active: number;
    paused: number;
    cancelled: number;
    pending: number;
  };
  revenue: {
    mrr: number;
    month_revenue: number;
    last_month_revenue: number;
    revenue_change: number;
    revenue_change_percent: number;
  };
  churn: {
    new_subscriptions: number;
    cancelled_subscriptions: number;
    net_change: number;
  };
  payments: {
    total_payments: number;
    failed_payments: number;
    failure_rate: number;
    upcoming_payments_count: number;
    upcoming_amount: number;
  };
  alerts: {
    overdue_count: number;
    overdue_amount: number;
    expiring_billing_keys: number;
  };
}

export const PaymintStatsCard: React.FC = () => {
  const [stats, setStats] = useState<PaymintStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPaymintStats();
  }, []);

  const fetchPaymintStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/v1/admin/payment/dashboard-stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Paymint 통계 조회 실패');
      }

      const result = await response.json();
      if (result.success) {
        setStats(result.data);
      } else {
        throw new Error(result.message || '알 수 없는 오류');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Paymint 통계 조회 중 오류 발생');
      console.error('Paymint Stats Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return amount.toLocaleString('ko-KR') + '원';
  };

  const formatPercentage = (value: number) => {
    return value >= 0 ? `+${value}%` : `${value}%`;
  };

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">💳 Paymint 구독 현황</h2>
          <p className="text-sm text-gray-600 mt-1">B2B 구독 결제 시스템 모니터링</p>
        </div>
        <button
          onClick={fetchPaymintStats}
          className="px-4 py-2 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 transition"
        >
          🔄 새로고침
        </button>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* MRR */}
        <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-indigo-600 text-xs font-medium">월간 반복 수익 (MRR)</p>
              <p className="text-2xl font-bold text-indigo-900 mt-1">
                {formatCurrency(stats.revenue.mrr)}
              </p>
            </div>
            <div className="text-3xl">📊</div>
          </div>
        </div>

        {/* Active Subscriptions */}
        <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-600 text-xs font-medium">활성 구독</p>
              <p className="text-2xl font-bold text-green-900 mt-1">
                {stats.subscriptions.active}
                <span className="text-sm text-gray-600 ml-1">/ {stats.subscriptions.total}</span>
              </p>
            </div>
            <div className="text-3xl">✅</div>
          </div>
        </div>

        {/* This Month Revenue */}
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-600 text-xs font-medium">이번 달 수익</p>
              <p className="text-2xl font-bold text-blue-900 mt-1">
                {formatCurrency(stats.revenue.month_revenue)}
              </p>
              <p className={`text-xs mt-1 ${stats.revenue.revenue_change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatPercentage(stats.revenue.revenue_change_percent)} (전월 대비)
              </p>
            </div>
            <div className="text-3xl">💰</div>
          </div>
        </div>

        {/* Payment Failure Rate */}
        <div className={`bg-gradient-to-br p-4 rounded-lg ${
          stats.payments.failure_rate > 5
            ? 'from-red-50 to-red-100'
            : 'from-gray-50 to-gray-100'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-xs font-medium ${
                stats.payments.failure_rate > 5 ? 'text-red-600' : 'text-gray-600'
              }`}>
                결제 실패율
              </p>
              <p className={`text-2xl font-bold mt-1 ${
                stats.payments.failure_rate > 5 ? 'text-red-900' : 'text-gray-900'
              }`}>
                {stats.payments.failure_rate}%
              </p>
              <p className="text-xs text-gray-600 mt-1">
                {stats.payments.failed_payments} / {stats.payments.total_payments}건
              </p>
            </div>
            <div className="text-3xl">{stats.payments.failure_rate > 5 ? '🚨' : '📈'}</div>
          </div>
        </div>
      </div>

      {/* Churn & Growth */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-gray-50 p-4 rounded-lg">
          <p className="text-sm text-gray-600 mb-2">신규 구독</p>
          <p className="text-xl font-bold text-green-600">+{stats.churn.new_subscriptions}</p>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg">
          <p className="text-sm text-gray-600 mb-2">해지 구독</p>
          <p className="text-xl font-bold text-red-600">-{stats.churn.cancelled_subscriptions}</p>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg">
          <p className="text-sm text-gray-600 mb-2">순 증감</p>
          <p className={`text-xl font-bold ${
            stats.churn.net_change >= 0 ? 'text-green-600' : 'text-red-600'
          }`}>
            {stats.churn.net_change >= 0 ? '+' : ''}{stats.churn.net_change}
          </p>
        </div>
      </div>

      {/* Alerts Section */}
      {(stats.alerts.overdue_count > 0 || stats.alerts.expiring_billing_keys > 0) && (
        <div className="border-t border-gray-200 pt-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">⚠️ 주의 필요</h3>
          <div className="space-y-2">
            {stats.alerts.overdue_count > 0 && (
              <div className="bg-red-50 border border-red-200 p-3 rounded">
                <p className="text-sm text-red-800">
                  <span className="font-semibold">연체 구독:</span> {stats.alerts.overdue_count}개
                  <span className="text-xs ml-2">
                    ({formatCurrency(stats.alerts.overdue_amount)})
                  </span>
                </p>
              </div>
            )}
            {stats.alerts.expiring_billing_keys > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 p-3 rounded">
                <p className="text-sm text-yellow-800">
                  <span className="font-semibold">빌링키 만료 임박:</span> {stats.alerts.expiring_billing_keys}개
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Subscription Status Breakdown */}
      <div className="border-t border-gray-200 pt-4 mt-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">구독 상태 분포</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="text-center">
            <p className="text-xs text-gray-600">활성</p>
            <p className="text-lg font-bold text-green-600">{stats.subscriptions.active}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-600">일시정지</p>
            <p className="text-lg font-bold text-orange-600">{stats.subscriptions.paused}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-600">대기</p>
            <p className="text-lg font-bold text-gray-600">{stats.subscriptions.pending}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-600">취소됨</p>
            <p className="text-lg font-bold text-red-600">{stats.subscriptions.cancelled}</p>
          </div>
        </div>
      </div>

      {/* Upcoming Payments */}
      {stats.payments.upcoming_payments_count > 0 && (
        <div className="border-t border-gray-200 pt-4 mt-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">다음 7일 내 예정 결제</h3>
          <div className="bg-blue-50 border border-blue-200 p-3 rounded">
            <p className="text-sm text-blue-800">
              <span className="font-semibold">{stats.payments.upcoming_payments_count}건</span>
              <span className="ml-2">
                ({formatCurrency(stats.payments.upcoming_amount)})
              </span>
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
